import Component from '@glimmer/component';
import { uniqueId } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import type { ComponentLike } from '@glint/template';
import { PhArrowRight } from 'ember-phosphor-icons';
import Card from 'tio-ui/components/card';

interface BenefitCardSignature {
  Args: {
    /** Program or benefit name, the card's h3. */
    title: string;
    /** 16px glyph before the title. */
    icon: ComponentLike<{ Element: SVGElement }>;
    /** Header "Action →" link text; the link renders only with `@actionRoute` too. */
    actionLabel?: string;
    /** Route the header link opens. */
    actionRoute?: string;
    /** Dynamic segment for `@actionRoute`, when the route has one. */
    actionModel?: string | number;
  };
  Blocks: {
    /**
     * The card body. The Figma benefit bodies (progress: number above label;
     * single metric: brand-coloured number) are the caller's; a `HeroStat`
     * approximates them but follows the hero card anatomy.
     */
    default: [];
  };
  Element: HTMLDivElement;
}

/** Figma `Benefit Card` shell for the Active Benefits / Your Programs section. */
export default class BenefitCard extends Component<BenefitCardSignature> {
  get actionModels() {
    const { actionModel } = this.args;
    return actionModel === undefined ? [] : [actionModel];
  }

  <template>
    {{#let (uniqueId) as |titleId|}}
      <Card
        @radius="nested"
        @padding="sm"
        class="tio-col gap-8 border tio-border-default"
        ...attributes
      >
        <div class="tio-row-between">
          <div class="tio-row-tight">
            {{#let @icon as |Icon|}}
              <Icon class="tio-icon-sm" />
            {{/let}}
            <h3 id={{titleId}} class="tio-text-base-semibold">{{@title}}</h3>
          </div>
          {{#if @actionRoute}}
            {{#if @actionLabel}}
              <LinkTo
                @route={{@actionRoute}}
                @models={{this.actionModels}}
                class="tio-row-tight tio-text-base-semibold app-text-link"
                aria-describedby={{titleId}}
              >
                {{@actionLabel}}
                <PhArrowRight class="tio-icon-md" />
              </LinkTo>
            {{/if}}
          {{/if}}
        </div>
        {{yield}}
      </Card>
    {{/let}}
  </template>
}
